/****************************************************
 * Module Name:  addEditUserDocument.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Tooltip, Typography } from "@mui/material";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { Form, Modal } from "react-bootstrap";
import { LoadingButton } from "@mui/lab";
import { SimCardDownloadSharp } from "@mui/icons-material";

import Select from "react-select";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import logo from '../../images/pansuriya.jpg'
import Constants from "../../utility/constants";
import TabMenuSnackbar from "../common/tabMenuSnackbar";
import renderCellExpand from "../common/dataGridCellExpand";

import DocumentTyeProxy from "../../utility/proxy/manager/documentTypeProxy";
import UserDocumentProxy from "../../utility/proxy/manager/userDocumentProxy";


export default function AddEditUserDocument(userId) {
    const [docData, setDocData] = useState([]);
    const [typeOptions, setTypeOptions] = useState([]);
    const [selectedType, setSelectedType] = useState("");
    const [docId, setDocId] = useState([]);

    const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
    const [changeOperation, setChangeOperation] = useState(false);
    const [sourceData, setSourceData] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileUploadLoading, setFileUploadLoading] = useState(false);

    const [fetchUserDocumentDataLoading, setFetchUserDocumentDataLoading] = useState(true);
    const [fetchDocumentTypeDataLoading, setFetchDocumentTypeDataLoading] = useState(true);

    const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");

    useEffect(() => {
        fetchDocData(false);
        fetchDocTypeData(false);
    }, [])

    const fetchDocData = (bAnimateSpinner) => {
        bAnimateSpinner && setFetchUserDocumentDataLoading(true);
        UserDocumentProxy.getDocumentByUserID(
            userId.userId, (responseData) => {
                if (responseData) {
                    //Success
                    setDocData(responseData);
                    let tempArray = responseData.map((item) => {
                        return item.docid;
                    })
                    setDocId(tempArray);
                    setFetchUserDocumentDataLoading(false);
                }
                else {
                    //FAILURE
                    setDocData([]);
                    setFetchUserDocumentDataLoading(false);
                    setShowSuccessSnackbar(true);
                    setSuccessMessage(Constants.APiFailErrorMessage);
                    setSnackbarVariant("error");
                }
            })
    }

    const fetchDocTypeData = (bAnimateSpinner) => {
        bAnimateSpinner && setFetchDocumentTypeDataLoading(true);
        DocumentTyeProxy.getAllDocumentType((responseData) => {
            if (responseData) {
                //Success
                setTypeData(responseData);
                setFetchDocumentTypeDataLoading(false);
            }
            else {
                //FAILURE
                setTypeData([]);
                setFetchDocumentTypeDataLoading(false);
                setShowSuccessSnackbar(true);
                setSuccessMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        })
    }

    const setTypeData = (responseData) => {
        let optionsArray = [];
        if (responseData) {
            responseData.map((data) => {
                let option = {
                    value: data.id,
                    label: data.type,
                }
                optionsArray.push(option);
                return true;
            })

            setTypeOptions(optionsArray);
        }
    }

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setSelectedFile(file);
    }

    const handleUploadFile = () => {
        let data = new FormData();
        data.append("file", selectedFile);
        if (changeOperation) {
            setFileUploadLoading(true);
            UserDocumentProxy.updateUserDocument(
                data, sourceData.docid, userId.userId,
                (response) => {
                    if (response) {
                        //Success
                        setSelectedType("");
                        fetchDocData(true);
                        setFileUploadLoading(false);
                        setChangeOperation(false);
                        setShowAddDocumentModal(false);
                        setShowSuccessSnackbar(true);
                        setSuccessMessage("Document Updated Successfully");
                        setSnackbarVariant("success");
                    } else {
                        //Failure
                        setFileUploadLoading(false);
                        setShowSuccessSnackbar(true);
                        setSuccessMessage(Constants.APiFailErrorMessage);
                        setSnackbarVariant("error");
                    }
                }
            )
        } else {
            UserDocumentProxy.createUserDocument(
                data, selectedType.value, userId.userId,
                (response) => {
                    if (response) {
                        setSelectedType("");
                        setFileUploadLoading(false);
                        setShowAddDocumentModal(false);
                        setChangeOperation(false);
                        fetchDocData(true);
                        setShowSuccessSnackbar(true);
                        setSuccessMessage("Document Added Successfully");
                        setSnackbarVariant("success");
                    } else {
                        //Failure
                        setFileUploadLoading(false);
                        setShowSuccessSnackbar(true);
                        setSuccessMessage(Constants.APiFailErrorMessage);
                        setSnackbarVariant("error");
                    }
                }
            )
        }
    };

    const handleDocDownload = (data) => {
        let a = document.createElement("a");
        a.href = `data:application/;base64,${data.doc}`
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
    }

    const columnsTable = [
        {
            field: "doctype",
            headerName: "DOCUMENT TYPE",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "doc",
            headerName: "DOWNLOAD",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: (params) => (
                <Box
                    style={{ textAlign: "center", cursor: "pointer" }}
                    onClick={() => handleDocDownload(params.row)}>
                    <SimCardDownloadSharp size={25} color="success" />
                </Box>
            ),
        },
        {
            field: "edit",
            headerName: "EDIT",
            headerAlign: "center",
            align: "center",
            flex: 0.5,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            setShowAddDocumentModal(true)
                            setChangeOperation(true)
                            setSourceData(params.row)
                        }}>
                        <BorderColorIcon color="primary" />
                    </Box>
                );
            },
        },
    ]

    const rows = docData;
    const columns = columnsTable;

    return (
        <div>
            {showSuccessSnackbar ? (
                <TabMenuSnackbar
                    show={true}
                    message={successMessage}
                    variant={snackbarVariant}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={snackbarVariant === "success" ? "Success" : "Failed"}
                    handleClose={() => setShowSuccessSnackbar(false)}
                />
            ) : null}
            {showAddDocumentModal ? (
                <Modal centered show={showAddDocumentModal} onHide={() => setShowAddDocumentModal(false)} enforceFocus={false}>
                    <Modal.Header closeButton onHide={() => setChangeOperation(false)}>
                        <Modal.Title>
                            <img
                                src={logo}
                                height="40pt"
                                width="40pt"
                                alt={Constants.COMPANY_SITE}
                            />
                            &nbsp;&nbsp;
                            {changeOperation
                                ? "Change Document"
                                : "Add New Document"}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Typography variant="h6">Choose a File to Upload Document</Typography>
                        <input
                            type="file"
                            accept=".pdf, .jpg, .jpeg, .png, .bmp, .heif, .heic"
                            multiple
                            onChange={(e) => handleFileChange(e)}
                        />
                        {changeOperation && sourceData ? sourceData.filename : null}
                    </Modal.Body>
                    <Modal.Footer>
                        <LoadingButton
                            loading={fileUploadLoading}
                            variant="contained"
                            onClick={handleUploadFile}
                            disabled={selectedFile === null}
                            sx={{ mr: 1 }}
                        >
                            Upload
                        </LoadingButton>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => {
                                setShowAddDocumentModal(false)
                                setChangeOperation(false)
                            }}
                        >
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            ) : null}

            <Box px={1} sx={{ mt: 1 }}>
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "100%",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        height: "60vh",
                    }}>
                    <DataGridPro
                        disableRowSelectionOnClick={true}
                        getRowHeight={() => "auto"}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "4px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "12px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "18px",
                            },

                        }}
                        density="compact"
                        slots={{ toolbar: GridToolbar }}
                        rows={rows}
                        columns={columns}
                        getRowId={(row) => row.docid}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns status and traderName, the other columns will remain visible
                                    docid: false,
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        loading={fetchUserDocumentDataLoading}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                    />

                    <Box style={{ display: "flex", position: "absolute", top: 12, right: 270 }}>
                        {fetchUserDocumentDataLoading ? (
                            <CircularProgress size="1rem" />
                        ) : (
                            <Tooltip title="Reload User Document List">
                                <RefreshOutlinedIcon
                                    color="primary"
                                    fontSize="medium"
                                    onClick={() => fetchDocData(true)}
                                    sx={{ cursor: "pointer" }}
                                />
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', mt: 1.2 }}>
                <Form.Label className="mt-2" style={{ paddingRight: '10px' }}>Document Type : </Form.Label>
                <Select menuPlacement="auto"
                    blurInputOnSelect={false}
                    className="basic-single"
                    classNamePrefix="select"
                    isSearchable={true}
                    name="Select Document Type"
                    menuPortalTarget={document.body}
                    styles={Constants.Styles}
                    value={selectedType}
                    onChange={(type) => setSelectedType(type)}
                    options={typeOptions.filter((item) => {
                        return !docId.includes(item.value);
                    })}
                />
                {fetchDocumentTypeDataLoading ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload Document Type List">
                        <RefreshOutlinedIcon
                            color="primary"
                            fontSize="medium"
                            onClick={() => fetchDocTypeData(true)}
                            sx={{ cursor: "pointer", alignSelf: 'center' }}
                        />
                    </Tooltip>
                )}

                <Tooltip title="Add New Document">
                    <Button
                        color="info"
                        variant="outlined"
                        size="small"
                        sx={{ ml: "10px" }}
                        onClick={() => {
                            setShowAddDocumentModal(true)
                        }}
                        startIcon={<AddBoxOutlinedIcon size="large" />}
                        disabled={typeOptions.length === 0 || selectedType === ""}
                    >
                        Add New Document
                    </Button>
                </Tooltip>
            </Box>
        </div>
    )
}